import React, { useEffect, Suspense, lazy } from 'react';
import { Route, Navigate, Routes, useLocation } from 'react-router-dom';
import { useSelector } from 'react-redux';
import classNames from 'classnames';

import { Aside } from './Aside';
import { MobileHeader } from './components/MobileHeader';
import { useLayoutStore } from 'stores/useLayoutStore';
import { account } from '@redux/account';

import { SizeProvider } from 'components/context/SizeProvider';
import { Tutorial } from 'components/Tutorial/Tutorial';
import { useDispatch } from 'utils/useDispatch';
import { useAuth } from 'utils/useauth';
import { useTutorialStore } from 'stores/useTutorialStore';
const Settings = lazy(() => import('../Settings/Settings').then((m) => ({ default: m.Settings })));
const IdeasWrapper = lazy(() => import('../Ideas/IdeasWrapper').then((m) => ({ default: m.IdeasWrapper })));
const DevicesContainer = lazy(() => import('../Devices/Container').then((m) => ({ default: m.DevicesContainer })));
const PlansContainer = lazy(() => import('../Plans/Container').then((m) => ({ default: m.PlansContainer })));
const PlaylistsContainer = lazy(() => import('../Playlists/Container').then((m) => ({ default: m.PlaylistsContainer })));
const SportLayout = lazy(() => import('../Sport/SportLayout').then((m) => ({ default: m.SportLayout })));
const AnyPaySuccess = lazy(() => import('../Plans/components/AnyPayResult').then((m) => ({ default: m.AnyPaySuccess })));
const AnyPayError = lazy(() => import('../Plans/components/AnyPayResult').then((m) => ({ default: m.AnyPayError })));
const PlategaSuccess = lazy(() => import('../Plans/components/PlategaResult').then((m) => ({ default: m.PlategaSuccess })));
const PlategaError = lazy(() => import('../Plans/components/PlategaResult').then((m) => ({ default: m.PlategaError })));

export const Layout = () => {
  const auth = useAuth();
  const location = useLocation();
  const { isTutorialShown, handleCloseTutorial } = useTutorialStore();

  const { sidebarCollapse: isCollapsed } = useLayoutStore();
  const isAuthenticated = useSelector(account.selectors.isAuthenticated);
  const settings = useSelector(account.selectors.settings);
  const user = useSelector(account.selectors.user);
  const fetchSettings = useDispatch(account.actions.fetchSettings);
  const fetchUser = useDispatch(account.actions.fetchUser);
  const language = useSelector(account.selectors.language);

  useEffect(() => {
    if (isAuthenticated && !user?.id) {
      fetchUser(); // This will now also update devices store
    }
  }, [user, isAuthenticated, fetchUser]);

  useEffect(() => {
    if (isAuthenticated && !settings) {
      fetchSettings();
    }
  }, [language, isAuthenticated, settings, fetchSettings]);

  if (!auth.isAuthenticated)
    return <Navigate to="/auth/login" state={{ from: location }} />;

  return (
    <div
      className={classNames({
        'overflow-hidden md:overflow-visible': !isCollapsed,
      })}
    >
      <SizeProvider>
        <Aside />
      </SizeProvider>
      {/*
        * App shell: the column is exactly one viewport tall (dvh, so iOS Safari's toolbar
        * is accounted for) and never scrolls itself — <main> below is the ONE scroll
        * container for every route. This replaces the previous model, where the document
        * scrolled and each route that wanted an internal scroller had to subtract the
        * header and gutter from 100dvh by hand. That model also double-scrolled on phones:
        * `min-h-screen` is 100vh, which on iOS is the viewport with the toolbar HIDDEN, so
        * the document was always taller than the visible area by the toolbar's height — the
        * page could be dragged up under the header, leaving a dead band below the console.
        */}
      <div
        id="app-main"
        className={classNames(
          'flex h-[100dvh] flex-col overflow-hidden bg-ui-bg text-ui-3 w-screen transition-all',
          {
            'md:pl-aside-collapsed': isCollapsed,
            'md:pl-50': !isCollapsed,
          },
        )}
      >
        <MobileHeader />
        {/* The single scroll region. A flex column with a definite height, so a route root
            can size itself with `flex-1` (fill the viewport, e.g. Settings, Sport, Ideas) or
            `flex-1 min-h-0` (fill it and let a child scroll internally — the playlist
            console). Routes that are simply long (Plans, Devices) overflow and this scrolls —
            see the `#content > *` rule in index.css, which stops a route root from shrinking
            to fit instead (a root with overflow-hidden, like Devices, has no automatic
            minimum height as a flex item and was silently clipped rather than scrolled).
            Sticky elements inside a route stick to THIS region's padding edge, not the
            viewport: a `sticky top-0` rests 1rem (1.5rem from md) below the header, so a
            route that wants to pin something flush under the header uses `-top-4` /
            `md:-top-6` (Settings' tab strip does). */}
        {/* Side gutter is 1rem at every width, top and bottom 1.5rem from md up — as PADDING, not margin, so the
            scrollable area itself is full-bleed (the gutter scrolls with the content instead
            of clipping it). Routes that break out of it with negative margins (Settings,
            Sport, Ideas) mirror these exact values. */}
        {/* overflow-x-hidden is deliberate: `overflow-y: auto` alone makes overflow-x `auto`
            too, and WebKit counts a route root's negative side margins (Settings, Sport,
            Ideas break out of the gutter that way) as horizontal overflow, which made the
            whole page pan sideways on iPhone. Anything that legitimately scrolls sideways
            (chip rails, billing tables) is its own overflow-x-auto container. */}
        <main id="content" className="flex min-h-0 flex-1 flex-col overflow-x-hidden overflow-y-auto overscroll-y-contain px-4 py-4 md:py-6">
          <Suspense fallback={null}>
            <Routes>
              <Route path="playlists/*" element={<PlaylistsContainer />} />
              <Route path="plans" element={<PlansContainer />} />
              <Route path="anypay/success" element={<AnyPaySuccess />} />
              <Route path="anypay/error" element={<AnyPayError />} />
              <Route path="platega/success" element={<PlategaSuccess />} />
              <Route path="platega/error" element={<PlategaError />} />
              <Route path="devices" element={<DevicesContainer />} />
              <Route path="sport/*" element={<SportLayout />} />
              <Route path="ideas" element={<IdeasWrapper />} />
              <Route path="settings" element={<Settings />} />
            </Routes>
          </Suspense>
        </main>
      </div>
      {!isTutorialShown && <Tutorial onClose={handleCloseTutorial} />}
    </div>
  );
};
